import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useBrand } from './useBrand'
import ConnectTikTokModal from '../components/ConnectTikTokModal'

/* ==================================================================
 * TiktokConnectProvider — single global home for the "connect your
 * TikTok Shop" prompt.
 * ==================================================================
 *
 * Two jobs:
 *   1. On every fresh page load (post-auth, post-onboarding), if the
 *      brand has not connected their TikTok Shop yet, auto-open the
 *      connect modal once — for admins/editors only, since a viewer
 *      can't act on it. The modal is dismissable — closing it
 *      remembers the choice for the current tab session only, so a
 *      hard refresh re-prompts.
 *
 *   2. Expose a tiny imperative API any feature can call to gate
 *      itself behind shop connection:
 *
 *        const { requireConnection } = useTiktokConnect()
 *
 *        const handleClick = () => {
 *          if (!requireConnection({ feature: 'sync orders' })) return
 *          // ... feature code, runs only when already connected
 *        }
 *
 *      `requireConnection` returns true if the brand is connected,
 *      false otherwise (and pops the modal in the false case).
 *
 * Mount once near the top of the authenticated tree (App.tsx) so
 * exactly one modal instance exists app-wide regardless of route.
 * ================================================================== */

interface TiktokConnectCtx {
  /** True when brand.is_shop_connected. Components can use this for
   *  presentational state (disabled buttons, banners, etc.). */
  isConnected: boolean
  /** Imperatively open the modal. Optional `feature` label is rendered
   *  inside the modal so the user understands which feature triggered
   *  the prompt (e.g. "Required to sync orders"). */
  openModal: (opts?: { feature?: string }) => void
  /** Close the modal programmatically. Same effect as the user clicking
   *  the X / Maybe later. */
  closeModal: () => void
  /** Gate helper: returns true if already connected (caller proceeds);
   *  returns false otherwise AND opens the modal so the user can
   *  connect. Pair with an early `return` in the click handler. */
  requireConnection: (opts?: { feature?: string }) => boolean
}

const TiktokConnectContext = createContext<TiktokConnectCtx | null>(null)

export function TiktokConnectProvider({ children }: { children: React.ReactNode }) {
  const { brand, loading, canEdit } = useBrand()
  const isConnected = !!brand?.is_shop_connected

  const [modalOpen, setModalOpen] = useState(false)
  const [feature, setFeature] = useState<string | undefined>(undefined)
  // In-memory dismissal flag (intentionally NOT sessionStorage). The user
  // asked for the prompt to re-fire on hard refresh, so we only suppress
  // the auto-open within a single React lifetime.
  const dismissedRef = useRef(false)
  const autoPromptedRef = useRef(false)

  // Auto-prompt once per page load when:
  //   - brand state has finished loading
  //   - we have an onboarded brand (no point prompting mid-onboarding)
  //   - the shop isn't connected
  //   - the signed-in user could actually connect it (admin/editor). Viewers
  //     are read-only — the worker rejects the connect call — so auto-opening
  //     this on every page load handed them a CTA whose only outcome was a
  //     403. They still see the modal when a gated feature calls
  //     requireConnection, where it explains what's missing and who can fix
  //     it, but nothing nags them about it unprompted.
  //
  // No longer conditional on a paid plan. That check was there because
  // free-tier brands couldn't hire, and TikTok Shop is purely a hiring
  // dependency — but the free-platform pivot made 'free' the default hiring
  // tier, so skipping the prompt for them skipped it for nearly everyone.
  //   - we haven't already prompted this lifetime
  useEffect(() => {
    if (loading) return
    if (!brand?.id) return
    if (!brand.onboarding_completed) return
    if (!canEdit) return
    if (isConnected) return
    if (autoPromptedRef.current || dismissedRef.current) return
    autoPromptedRef.current = true
    setModalOpen(true)
  }, [loading, brand?.id, brand?.onboarding_completed, isConnected, canEdit])

  const openModal = useCallback((opts?: { feature?: string }) => {
    setFeature(opts?.feature)
    setModalOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setModalOpen(false)
    dismissedRef.current = true
    setFeature(undefined)
  }, [])

  const requireConnection = useCallback((opts?: { feature?: string }) => {
    if (isConnected) return true
    openModal(opts)
    return false
  }, [isConnected, openModal])

  const value = useMemo<TiktokConnectCtx>(
    () => ({ isConnected, openModal, closeModal, requireConnection }),
    [isConnected, openModal, closeModal, requireConnection]
  )

  return (
    <TiktokConnectContext.Provider value={value}>
      {children}
      {modalOpen && brand?.id && (
        <ConnectTikTokModal brandId={brand.id} feature={feature} onClose={closeModal} />
      )}
    </TiktokConnectContext.Provider>
  )
}

export function useTiktokConnect(): TiktokConnectCtx {
  const ctx = useContext(TiktokConnectContext)
  if (!ctx) {
    throw new Error('useTiktokConnect must be used inside <TiktokConnectProvider>')
  }
  return ctx
}
